import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import { CheckCircle, PencilSimple } from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router';
import { useTypedSelector } from '../reducers';
import { ScreenWrap, HeaderBreadcrumbs, SoloLoading, ScreenError } from '.';
import { Api } from '../api';
import UserSymptoms from './metrics/UserSymptoms';
import UserFoodAndWater from './metrics/UserFoodAndWater';
import UserMoodAndDailyThoughts from './metrics/UserMoodAndDailyThoughts';
import UserAppleHealthData from './metrics/UserAppleHealthData';
import EditMetricsModal from './modals/EditMetricsModal';
import PeriodToggle from './metrics/PeriodToggle';
import { useUserStatusContext } from '../contexts';

const METRIC_TABS = [
  { label: 'Symptoms & Cycle', value: 'symptoms-cycle' },
  { label: 'Food & Water', value: 'food-water' },
  {
    label: 'Mood & Daily Thoughts',
    value: 'mood-daily-thoughts',
  },
  { label: 'Apple Health Data', value: 'apple-health' },
];

export default function UserMetricsScreen() {
  const { hasEditAccess } = useUserStatusContext();
  const { id, tab = 'symptoms-cycle' } = useParams();
  const navigate = useNavigate();
  const { viewMode } = useTypedSelector((state) => state.auth);
  const statsApi = Api.UserStats.useReadItemStats(id || '');
  const metricSettingsApi = Api.UserMetricSetting.useReadItemMetricSettings(
    id || '',
  );

  const [editModalOpen, setEditModalOpen] = React.useState(false);

  if (statsApi.error || metricSettingsApi.error)
    return <ScreenError {...statsApi} />;
  if (!statsApi.data || !metricSettingsApi.data) return <SoloLoading />;

  const isAdmin = viewMode === 'admin';
  const basePath = '/users';

  const getTabEnabled = (tabValue: string) => {
    const settings = metricSettingsApi.data;
    if (!settings) return false;

    switch (tabValue) {
      case 'symptoms-cycle':
        return settings.symptoms || settings.cycleManual;
      case 'food-water':
        return settings.food || settings.water;
      case 'mood-daily-thoughts':
        return settings.mood || settings.dailyThoughts;
      case 'apple-health':
        return settings.steps || settings.exercise || settings.sleep;
      default:
        return false;
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    navigate(`${basePath}/${id}/metrics/${newValue}`);
  };

  const handleEditMetrics = () => {
    setEditModalOpen(true);
  };

  const renderTabContent = () => {
    switch (tab) {
      case 'symptoms-cycle':
        return <UserSymptoms />;
      case 'food-water':
        return <UserFoodAndWater />;
      case 'mood-daily-thoughts':
        return <UserMoodAndDailyThoughts />;
      case 'apple-health':
        return <UserAppleHealthData />;
      default:
        return <UserSymptoms />;
    }
  };

  return (
    <ScreenWrap>
      <Stack spacing={3}>
        <Box>
          <HeaderBreadcrumbs
            title="Metrics"
            items={[
              {
                name: isAdmin ? 'App Users' : 'Your Clients',
                url: basePath,
              },
              {
                name: statsApi.data.user.name || statsApi.data.user.email,
                url: `${basePath}/${id}/user-dashboard`,
              },
              {
                name: 'Metrics',
              },
            ]}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle size={24} />
            <Typography variant="body2" color="text.secondary">
              Metrics with this icon are currently enabled by the user
            </Typography>
            {hasEditAccess && (
              <Button
                variant="text"
                startIcon={<PencilSimple size={16} />}
                onClick={handleEditMetrics}
              >
                Edit enabled metrics
              </Button>
            )}
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {METRIC_TABS.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTabEnabled(t.value) && <CheckCircle size={24} />}
                  <Typography>{t.label}</Typography>
                </Box>
              }
            />
          ))}
        </Tabs>

        {!getTabEnabled(tab) && (
          <Alert severity="info" sx={{ bgcolor: '#DEE3E6' }} icon={false}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              This tracker is currently disabled
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The user is currently not seeing this metric tracker in their app
            </Typography>
            {hasEditAccess && (
              <Button
                variant="text"
                startIcon={<PencilSimple size={16} />}
                onClick={handleEditMetrics}
                sx={{ color: 'inherit', p: 0, justifyContent: 'flex-start' }}
              >
                Edit enabled metrics
              </Button>
            )}
          </Alert>
        )}

        <PeriodToggle />

        {renderTabContent()}
      </Stack>

      <EditMetricsModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
      />
    </ScreenWrap>
  );
}
